import { useState } from "react";

const GRID_SIZE = 10;
const ZOOM_FACTOR = 1.1;
const MIN_SCALE = 0.5;
const MAX_SCALE = 3;

export default function useActions(
  selectedSeats, setSelectedSeats,
  seatPositions, setSeatPositions,
  seatPositionsTemp, setSeatPositionsTemp,
  groupPosition, setGroupPosition,
  selectionBox, setSelectionBox,
  selectionStart, setSelectionStart,
  copiedSeats, setCopiedSeats,
  stageRef, history, setHistory,
  historyIndex, setHistoryIndex,
  backgroundImage, id, setLoading
) {

  const [scale, setScale] = useState(1);

  const handleRightClick = (e) => {
    e.evt.preventDefault();
    setSelectedSeats([]);
  };

  const handleMouseUp = () => {
    if (!selectionBox) return;
    
    const selected = seatPositions
      .map((seat) =>
        seat.x + 20 >= selectionBox.x &&
        seat.x + 20 <= selectionBox.x + selectionBox.width &&
        seat.y + 15 >= selectionBox.y &&
        seat.y + 15 <= selectionBox.y + selectionBox.height
          ? seat.id
          : null
      )
      .filter(Boolean);

    setSelectedSeats([...selectedSeats, ...selected]);
    setSelectionBox(null);
    setSelectionStart(null);
  };

  const handleRotateSeats = (degrees) => {
    let degRotation = degrees ? parseFloat(degrees) : 45
    const updatedSeats = seatPositions.map((seat) =>
      selectedSeats.includes(seat.id)
        ? { ...seat, rotation: (seat.rotation + degRotation ) % 360 }
        : seat
    );
    updateSeatPositions(updatedSeats);
  };

  const handleRotateCustom = () => {
    const degrees = parseFloat(prompt("Enter rotation degree:"));
    if (!degrees || degrees < 0 || isNaN(degrees) || degrees > 360) return;
    const updatedSeats = seatPositions.map((seat) =>
      selectedSeats.includes(seat.id)
        ? { ...seat, rotation: (seat.rotation + degrees) % 360 }
        : seat
    );
    updateSeatPositions(updatedSeats);
  };

  const handleRotateReset = () => {
    const updatedSeats = seatPositions.map((seat) =>
      selectedSeats.includes(seat.id)
        ? { ...seat, rotation: 0 }
        : seat
    );
    updateSeatPositions(updatedSeats);
  };

  const handleGrowSeat = () => {
    const updatedSeats = seatPositions.map((seat) =>
      selectedSeats.includes(seat.id)
        ? { ...seat, width: seat.width + 5, height: seat.height + 5 }
        : seat
    );
    updateSeatPositions(updatedSeats);
  };

  const handleReduceSeat = () => {
    const updatedSeats = seatPositions.map((seat) =>
      selectedSeats.includes(seat.id)
        ? { ...seat, width: seat.width - 5, height: seat.height - 5 }
        : seat
    );
    updateSeatPositions(updatedSeats);
  };

  const handleSelectSeat = (seatNumber) => {
    setSelectedSeats((prev) =>
      prev.includes(seatNumber) ? prev.filter((num) => num !== seatNumber) : [...prev, seatNumber]
    );
  };

  const handleCopy = () => {
    if (!selectedSeats.length) return;
    setCopiedSeats(seatPositions.filter(seat => selectedSeats.includes(seat.id)));
  };

  const handlePaste = () => {
    if (!copiedSeats.length) return;

    const newSeats = [];
    for(let i = 0; i < copiedSeats.length; i++){
      if(seatPositions.length + i < 500){
        newSeats.push({...copiedSeats[i],
        x: copiedSeats[i].x + 5 * GRID_SIZE,
        y: copiedSeats[i].y + 5 * GRID_SIZE,
        numero: 'New',
        id: Date.now()+i,
        isNew: true,
        sala: id
        })
      }
    }

    setSelectedSeats(newSeats.map((seat) => seat.id));
    updateSeatPositions([...seatPositions, ...newSeats]);
    setCopiedSeats([]);
  };

  const handleDragMove = (e) => {
    const deltaX = e.target.x() - groupPosition.x;
    const deltaY = e.target.y() - groupPosition.y;

    setGroupPosition({ x: e.target.x(), y: e.target.y() });
    setSeatPositionsTemp((prev) =>
      prev.map((pos) =>{
        if(selectedSeats.includes(pos.id)){
          let newX = pos.x + deltaX;
          let newY = pos.y + deltaY;
          if (newX < 0 || newX > backgroundImage.width || newY < 0 || newY > backgroundImage.height) {
            // handleUndo();
            return pos
          }else{
            return { ...pos, x: newX, y: newY }
          }
        }else{
          return pos
        }
      })
    );
  };

  const handleDragEnd = () => {
    updateSeatPositions(
      seatPositionsTemp.map((pos) => ({
        ...pos,
        x: Math.round(pos.x / GRID_SIZE) * GRID_SIZE,
        y: Math.round(pos.y / GRID_SIZE) * GRID_SIZE,
      }))
    );
    setGroupPosition({ x: 0, y: 0 });
  };

  const handleMouseDown = (e) => {
    const stage = stageRef.current;
    const pointer = stage.getPointerPosition();
    const stagePos = stage.position();
    const scale = stage.scaleX();

    const x = (pointer.x - stagePos.x) / scale;
    const y = (pointer.y - stagePos.y) / scale;

    setSelectionStart({ x, y });
    setSelectionBox({ x, y, width: 0, height: 0 });
  };

  const handleMouseMove = (e) => {
    if (!selectionBox || !selectionStart) return;
    const stage = stageRef.current;
    const pointer = stage.getPointerPosition();
    const stagePos = stage.position();
    const scale = stage.scaleX();

    const x = (pointer.x - stagePos.x) / scale;
    const y = (pointer.y - stagePos.y) / scale;

    setSelectionBox({
      x: Math.min(selectionStart.x, x),
      y: Math.min(selectionStart.y, y),
      width: Math.abs(x - selectionStart.x),
      height: Math.abs(y - selectionStart.y),
    });
  };

  const handleZoom = (e) => {
    if (!e.evt.ctrlKey) return;
    e.evt.preventDefault();

    const stage = stageRef.current;
    const oldScale = stage.scaleX();
    const pointer = stage.getPointerPosition();
    const mousePointTo = {
      x: (pointer.x - stage.x()) / oldScale,
      y: (pointer.y - stage.y()) / oldScale,
    };

    let newScale = e.evt.deltaY > 0 ? oldScale / ZOOM_FACTOR : oldScale * ZOOM_FACTOR;
    newScale = Math.max(MIN_SCALE, Math.min(newScale, MAX_SCALE));

    setScale(newScale);

    const newPos = {
      x: pointer.x - mousePointTo.x * newScale,
      y: pointer.y - mousePointTo.y * newScale,
    };

    stage.scale({ x: newScale, y: newScale });
    stage.position(newPos);
    stage.batchDraw();
  };

  const zoomIn = () => {
    const stage = stageRef.current;
    let newScale = Math.min(scale * ZOOM_FACTOR, MAX_SCALE);
    setScale(newScale);
    stage.scale({ x: newScale, y: newScale });
    stage.batchDraw();
  };

  const zoomOut = () => {
    const stage = stageRef.current;
    let newScale = Math.max(scale / ZOOM_FACTOR, MIN_SCALE);
    setScale(newScale);
    stage.scale({ x: newScale, y: newScale });
    stage.batchDraw();
  };

  const zoomReset = () => {
    const stage = stageRef.current;
    let newScale = 1;
    setScale(newScale);
    stage.scale({ x: newScale, y: newScale });
    stage.position({ x: 0, y: 0 });
    stage.batchDraw();
  };

  const handleEdit = () => {
    if (selectedSeats.length > 1) return;

    const newName = prompt("Enter new seat Name:");
    if (!newName) return;

    updateSeatPositions((prevSeats) => 
      prevSeats.map((seat) => 
        seat.id === selectedSeats[0] 
          ? { ...seat, numero: newName } 
          : seat
      )
    );
  };

  const handleEditRow = () => {
    if (selectedSeats.length > 0){
      const newRow = prompt("Enter new row:");
      if (!newRow) return;
  
      updateSeatPositions((prevSeats) => 
        prevSeats.map((seat) => 
          selectedSeats.includes(seat.id)
          ? { ...seat, row: newRow} : seat
        )
      );
    }
  };

  const handleEditColumn = () => {
    if (selectedSeats.length > 0){
      const newColumn = prompt("Enter new column:");
      if (!newColumn) return;
  
      updateSeatPositions((prevSeats) => 
        prevSeats.map((seat) => 
          selectedSeats.includes(seat.id)
          ? { ...seat, column: newColumn} : seat
        )
      );
    }
  };

  const handleMirrorSeats = () => {
    if (selectedSeats.length === 0) return;

    const selectedSeatsData = seatPositions.filter(seat => selectedSeats.includes(seat.id));
    const minX = Math.min(...selectedSeatsData.map(seat => seat.x));
    const maxX = Math.max(...selectedSeatsData.map(seat => seat.x));
    const centerX = (minX + maxX) / 2;
  
    const updatedSeats = seatPositions.map(seat =>
      selectedSeats.includes(seat.id)
        ? { 
            ...seat, 
            x: centerX - (seat.x - centerX), 
            rotation: (360 - seat.rotation) % 360
          } 
        : seat
    );
  
    updateSeatPositions(updatedSeats)
  };

  const handleMirrorSeatsVertically = () => {
    if (selectedSeats.length === 0) return;

    const selectedSeatsData = seatPositions.filter(seat => selectedSeats.includes(seat.id));
    const minY = Math.min(...selectedSeatsData.map(seat => seat.y));
    const maxY = Math.max(...selectedSeatsData.map(seat => seat.y));
    const centerY = (minY + maxY) / 2;
  
    const updatedSeats = seatPositions.map(seat =>
      selectedSeats.includes(seat.id)
        ? { 
            ...seat, 
            y: centerY - (seat.y - centerY),
            rotation: (180-seat.rotation) % 360
          } 
        : seat
    );
  
    updateSeatPositions(updatedSeats);
  };

  const updateSeatPositions = (newSeats) => {
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(newSeats);
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
    setSeatPositions(newSeats);
    setSeatPositionsTemp(newSeats)
  };

  const handleUndo = () => {
    if (historyIndex > 0) {
      const prevIndex = historyIndex - 1;
      setHistoryIndex(prevIndex);
      setSeatPositions(history[prevIndex]);
      setSeatPositionsTemp(history[prevIndex]);
    }
  };

  const handleRedo = () => {
    if (historyIndex < history.length - 1) {
      const nextIndex = historyIndex + 1;
      setHistoryIndex(nextIndex);
      setSeatPositions(history[nextIndex]);
      setSeatPositionsTemp(history[nextIndex]);
    }
  };

  const handleAddMultipleSeats = () => {
    const newSeats = []
    const quantity = parseFloat(prompt("Enter seats quantity:"));
    if (quantity <= 0 || quantity > 20) return;

    for(let i = 0; i < quantity; i++){
      if(seatPositions.length + i < 500){
        let seat ={
        id: Date.now() + i, 
        x: (i * 50) + 100, 
        y: 50,
        width: 40,
        height: 40,
        row: 0,
        column: 0,
        rotation: 0,
        numero: 'New',
        sala: id,
        isNew: true
        }
        newSeats.push(seat);
      }
    }

    setSelectedSeats(newSeats.map(seat=>seat.id));
    updateSeatPositions([...seatPositions, ...newSeats]);
  };

  const handleRemoveSelectedSeats = () => {
    updateSeatPositions(seatPositions.filter(seat => !selectedSeats.includes(seat.id)));
    setSelectedSeats([]);
  };

  const handleSave = async (api) => {
    setLoading(true);
    for(let seat of seatPositions){
      if(!!seat.isNew){
        await api.createAsiento(seat);
      } else{
        await api.updateAsiento(seat);
      }
    }
    setLoading(false);
  };

  return {
    handleRightClick,
    handleRotateSeats,
    handleRotateCustom,
    handleRotateReset,
    handleMouseUp,
    handleSelectSeat,
    handleCopy,
    handlePaste,
    handleDragMove,
    handleDragEnd,
    handleMouseDown,
    handleMouseMove,
    handleZoom,
    zoomIn,
    zoomOut,
    handleEdit,
    zoomReset,
    handleMirrorSeats,
    handleMirrorSeatsVertically,
    handleUndo,
    handleRedo,
    handleAddMultipleSeats,
    handleRemoveSelectedSeats,
    handleReduceSeat,
    handleGrowSeat,
    handleSave,
    handleEditRow,
    handleEditColumn
  };
}
